import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../Firebase/Config';

export interface StateProvince {
  id: string;
  name: string;
  shippingCost: number;
  freeShippingLimit?: number; // State-level override; if explicitly set, takes priority over country-level
}

export interface Country {
  id: string;
  name: string;
  code: string;
  states: StateProvince[];
  defaultShippingCost: number;
  freeShippingLimit: number; // Country-level free shipping threshold (0 = disabled)
}

export interface ShippingCalculation {
  countryId: string;
  countryName: string;
  stateId?: string;
  stateName?: string;
  shippingCost: number;
  freeShippingLimit: number; // The applicable threshold for free shipping (0 = disabled)
}

export interface ShippingInfo {
  countryId: string;
  countryName?: string;   // stored for Checkout pre-population
  stateId?: string;
  stateName?: string;     // stored for Checkout pre-population
  postalCode?: string;
  shippingCost: number;
  freeShippingLimit?: number;
}

const SHIPPING_INFO_KEY = 'shipping_info';
const CACHE_KEY = 'shipping_countries_cache';
const CACHE_DURATION = 1000 * 60 * 30; // 30 minutes

// In-memory cache
let countriesCache: Country[] | null = null;
let cacheTimestamp: number | null = null;

// ---------------------------------------------------------------------------
// Firestore fetch
// ---------------------------------------------------------------------------
const fetchShippingRatesFromFirestore = async (): Promise<Country[]> => {
  try {
    const shippingRatesRef = collection(db, 'shippingRates');
    const q = query(shippingRatesRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);

    const countries: Country[] = snapshot.docs.map(doc => {
      const data = doc.data();

      // Parse states array carefully.
      // freeShippingLimit may be 0, a positive number, or absent.
      // We must NOT use "|| 0" because that would treat a valid 0 the same as
      // "not present". We check typeof === 'number' so any numeric value
      // (including 0 and 5000) is preserved exactly as stored in Firestore.
      const states: StateProvince[] = (data.states || []).map((state: any) => {
        const parsed: StateProvince = {
          id: state.id ?? '',
          name: state.name ?? '',
          shippingCost: typeof state.shippingCost === 'number' ? state.shippingCost : 0,
        };
        // Only attach freeShippingLimit when it is actually present on the map
        if (typeof state.freeShippingLimit === 'number') {
          parsed.freeShippingLimit = state.freeShippingLimit;
        }
        return parsed;
      });

      return {
        id: doc.id,
        name: data.name ?? '',
        code: data.code ?? '',
        defaultShippingCost:
          typeof data.defaultShippingCost === 'number' ? data.defaultShippingCost : 0,
        freeShippingLimit:
          typeof data.freeShippingLimit === 'number' ? data.freeShippingLimit : 0,
        states,
      };
    });

    // Update in-memory cache
    countriesCache = countries;
    cacheTimestamp = Date.now();

    // Persist to localStorage
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data: countries, timestamp: cacheTimestamp })
      );
    } catch (e) {
      console.warn('Failed to cache shipping data to localStorage:', e);
    }

    return countries;
  } catch (error) {
    console.error('Error fetching shipping rates from Firestore:', error);

    // Fall back to localStorage cache if Firestore fails
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data } = JSON.parse(cached);
        return data;
      }
    } catch (e) {
      console.warn('Failed to load cached shipping data:', e);
    }

    return [];
  }
};

// ---------------------------------------------------------------------------
// Public: get all countries (async, with cache)
// ---------------------------------------------------------------------------
export const getCountries = async (): Promise<Country[]> => {
  // Valid in-memory cache
  if (countriesCache && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_DURATION) {
    return countriesCache;
  }

  // Try localStorage cache before hitting Firestore
  if (!countriesCache) {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          countriesCache = data;
          cacheTimestamp = timestamp;
          return data;
        }
      }
    } catch (e) {
      console.warn('Failed to load cached shipping data:', e);
    }
  }

  return await fetchShippingRatesFromFirestore();
};

// ---------------------------------------------------------------------------
// Public: get countries synchronously (returns cached data or empty array)
// ---------------------------------------------------------------------------
export const getCountriesSync = (): Country[] => {
  if (countriesCache) return countriesCache;

  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data } = JSON.parse(cached);
      countriesCache = data;
      return data;
    }
  } catch (e) {
    console.warn('Failed to load cached shipping data:', e);
  }

  return [];
};

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------
export const getCountryById = (
  countryId: string,
  countries?: Country[]
): Country | undefined => {
  const data = countries || countriesCache || getCountriesSync();
  return data.find(c => c.id === countryId);
};

export const getStatesByCountry = (
  countryId: string,
  countries?: Country[]
): StateProvince[] => {
  const country = getCountryById(countryId, countries);
  return country?.states || [];
};

// ---------------------------------------------------------------------------
// calculateShipping
// ---------------------------------------------------------------------------
export const calculateShipping = (
  countryId: string,
  stateId?: string,
  postalCode?: string,
  countries?: Country[]
): ShippingCalculation => {
  const country = getCountryById(countryId, countries);

  if (!country) {
    return { countryId, countryName: 'Unknown', shippingCost: 0, freeShippingLimit: 0 };
  }

  // No state selected → use country-level values
  if (!stateId) {
    return {
      countryId: country.id,
      countryName: country.name,
      shippingCost: country.defaultShippingCost,
      freeShippingLimit: country.freeShippingLimit,
    };
  }

  // Find the specific state
  const state = country.states.find(s => s.id === stateId);

  if (!state) {
    // State ID provided but not found → fall back to country defaults
    return {
      countryId: country.id,
      countryName: country.name,
      shippingCost: country.defaultShippingCost,
      freeShippingLimit: country.freeShippingLimit,
    };
  }

  // State-level freeShippingLimit overrides country-level ONLY when the field
  // is explicitly present as a number (typeof check handles 0 correctly).
  const freeShippingLimit =
    typeof state.freeShippingLimit === 'number'
      ? state.freeShippingLimit   // state override (e.g. 5000)
      : country.freeShippingLimit; // fall back to country-level (e.g. 8000)

  return {
    countryId: country.id,
    countryName: country.name,
    stateId: state.id,
    stateName: state.name,
    shippingCost: state.shippingCost,
    freeShippingLimit,
  };
};

// ---------------------------------------------------------------------------
// qualifiesForFreeShipping
// freeShippingLimit === 0 means free shipping is DISABLED for this destination
// ---------------------------------------------------------------------------
export const qualifiesForFreeShipping = (
  orderTotal: number,
  freeShippingLimit: number
): boolean => {
  if (!freeShippingLimit || freeShippingLimit <= 0) return false;
  return orderTotal >= freeShippingLimit;
};

// ---------------------------------------------------------------------------
// Session-storage helpers
// ---------------------------------------------------------------------------
export const saveShippingInfo = (shippingInfo: ShippingInfo): void => {
  try {
    sessionStorage.setItem(SHIPPING_INFO_KEY, JSON.stringify(shippingInfo));
  } catch (error) {
    console.error('Error saving shipping info:', error);
  }
};

export const getShippingInfo = (): ShippingInfo | null => {
  try {
    const data = sessionStorage.getItem(SHIPPING_INFO_KEY);
    if (!data) return null;
    return JSON.parse(data);
  } catch (error) {
    console.error('Error reading shipping info:', error);
    return null;
  }
};

export const clearShippingInfo = (): void => {
  try {
    sessionStorage.removeItem(SHIPPING_INFO_KEY);
  } catch (error) {
    console.error('Error clearing shipping info:', error);
  }
};

// ---------------------------------------------------------------------------
// Cache invalidation (useful after admin updates)
// ---------------------------------------------------------------------------
export const clearShippingCache = (): void => {
  countriesCache = null;
  cacheTimestamp = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (e) {
    console.warn('Failed to clear shipping cache:', e);
  }
};

export const preloadCountries = async (): Promise<void> => {
  await getCountries();
};